import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Phone, Envelope, CheckCircle, XCircle } from 'phosphor-react';
import { contactService } from '../services/contactService';
import { useAuth } from '../context/AuthContext';

function Contact() {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [submitMessage, setSubmitMessage] = useState('');

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage('');

    try {
      // Get user agent and IP (simplified)
      const userAgent = navigator.userAgent;
      
      const messageData = {
        ...formData,
        user_id: user?.id || null,
        user_agent: userAgent
      };

      const result = await contactService.submitMessage(messageData);

      if (result.success) {
        setSubmitStatus('success');
        setSubmitMessage('Bedankt voor je bericht! We nemen zo snel mogelijk contact met je op.');
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
        setSubmitMessage('Er is een fout opgetreden. Probeer het later opnieuw of stuur een e-mail naar hallo@atelierambree.be');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      setSubmitMessage('Er is een fout opgetreden. Probeer het later opnieuw of stuur een e-mail naar hallo@atelierambree.be');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      {/* Contact Header */}
      <section className="py-20 px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">{t('contact.title')}</h1>
          <p className="text-lg text-gray-600">{t('contact.subtitle')}</p>
        </div>
      </section>
      
      {/* Contact Content */}
      <section className="flex-1 py-16 px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('contact.getInTouch')}</h2>
              
              <div className="space-y-6">
                {/* <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-ambre-100 rounded-lg flex items-center justify-center">
                    <Phone size={24} className="text-ambre-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{t('contact.phone')}</h3>
                    <p className="text-gray-600">+33 1 23 45 67 89</p>
                  </div>
                </div> */}
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-ambre-100 rounded-lg flex items-center justify-center">
                    <Envelope size={24} className="text-ambre-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{t('contact.email')}</h3>
                    <p className="text-gray-600">
                      <a 
                        href="mailto:hallo@atelierambree.be" 
                        className="text-ambre-600 hover:text-ambre-700 hover:underline transition"
                      >
                        hallo@atelierambree.be
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.sendMessage')}</h2>
              
              {/* Success/Error Messages */}
              {submitStatus && (
                <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
                  submitStatus === 'success' 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {submitStatus === 'success' ? (
                    <CheckCircle size={20} className="text-green-600" />
                  ) : (
                    <XCircle size={20} className="text-red-600" />
                  )}
                  <span className="text-sm font-medium">{submitMessage}</span>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">{t('contact.name')}</label>
                  <input 
                    type="text" 
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500" 
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">{t('contact.email')}</label>
                  <input 
                    type="email" 
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500" 
                  />
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">{t('contact.subject')}</label>
                  <input 
                    type="text" 
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500" 
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">{t('contact.message')}</label>
                  <textarea 
                    id="message"
                    name="message"
                    rows="4" 
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className={`w-full px-6 py-3 rounded-lg font-semibold transition ${
                    isSubmitting
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-ambre-600 hover:bg-ambre-700'
                  } text-white`}
                >
                  {isSubmitting ? 'Bezig met versturen...' : t('contact.send')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default Contact; 